import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faPlus,
  faDownload,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { CustomDropdown, MAIN_TAB_PAGE_LABEL, medPromptBrowserTitle, Table, ColumnCustomizationModal, ColumnCustomizationButton } from '@medprompt/shared';
import ReferralModal from './ReferralModal';
import SearchBar from '../detail/tabs/SearchBar';
import HelpButton from '../help/HelpButton';
import { HELP_PAGE_IDS } from '../help/helpContent';
import '@medprompt/shared/src/common/modals.css';
import '@medprompt/shared/src/common/dropdowns.css';
import './ReferralView.css';
import '../list-view-page.css';
import useReferralList from './useReferralList';
import useReferralTable from './useReferralTable';
import CreateReferralModal from './CreateReferralModal';
import DeleteReferralModal from './DeleteReferralModal';
import BulkMarkCompleteReferralsModal from './BulkMarkCompleteReferralsModal';

const ReferralViewController = () => {
  const list = useReferralList();
  const table = useReferralTable(list);

  useEffect(() => {
    document.title = medPromptBrowserTitle(MAIN_TAB_PAGE_LABEL.referrals);
  }, []);

  const handleRowClick = (referral) => {
    list.openReferral(referral);
    table.setHighlightedRows(new Set([referral.referralId]));
  };

  return (
    <div className="referral-view list-view-page">
      <div className="header">
        <div className="header-title-text">
          <div className="header-title-with-help">
            <h1 className="tab-page-title">{MAIN_TAB_PAGE_LABEL.referrals}</h1>
            <HelpButton pageId={HELP_PAGE_IDS.referrals} />
          </div>
          <CustomDropdown
            label="View Mode"
            selected={list.currentViewModeName}
            selectedIcon={list.currentViewModeIcon}
            options={list.viewModeOptions}
            isOpen={list.viewModeDropdown.isOpen}
            toggleDropdown={list.viewModeDropdown.toggle}
            onSelect={list.handleViewModeChange}
            containerRef={list.viewModeDropdown.dropdownRef}
            width="180px"
          />
        </div>
        <div className="header-buttons">
          {list.isSearchActive && (
            <button className="header-button clear-search-button" onClick={list.handleClearSearch}>
              <FontAwesomeIcon icon={faTimesCircle} /> Clear search
            </button>
          )}
          <SearchBar
            placeholder="Search for a patient's referrals"
            onSearch={list.handleReferralSearch}
            onResults={undefined}
            className="referrals-search-bar"
            clearSearch={list.setClearSearchBarFunction}
          />
          <button
            className="header-button"
            onClick={list.handleDownloadAllReferrals}
          >
            <FontAwesomeIcon icon={faDownload} /> Download Referrals
          </button>
          {list.hasDocsWrite && (
            <button
              className="header-button header-button-primary"
              onClick={() => list.setCreateModalOpen(true)}
            >
              <FontAwesomeIcon icon={faPlus} /> Add Case
            </button>
          )}
        </div>
      </div>

      {list.loading ? (
        <div className="loading-container">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" />
          <p>Loading referrals...</p>
        </div>
      ) : (
        <div className="referral-table-container">
          {list.isSearchActive && (
            <div className="referrals-search-results-info">
              Found {list.searchResults.length} referral{list.searchResults.length !== 1 ? 's' : ''} matching "{list.searchQuery}"
            </div>
          )}

          <Table
            id="ReferralsTable"
            data={list.displayReferrals}
            columns={table.columns}
            columnOrder={table.columnOrder}
            hiddenColumns={table.hiddenColumnsSet}
            settingsButton={<ColumnCustomizationButton onClick={() => table.setColumnCustomizationOpen(true)} />}
            getRowKey={(r) => r.referralId}
            sortState={table.tableSortState}
            onSortChange={table.setTableSortState}
            filterValues={table.tableFilterValues}
            onFilterChange={table.setTableFilterValues}
            disabledFilters={table.tableDisabledFilters}
            onDisabledFiltersChange={table.setTableDisabledFilters}
            paginationState={table.tablePaginationState}
            onPaginationChange={table.setTablePaginationState}
            onRowClick={handleRowClick}
            enableSelection={true}
            bulkActions={table.bulkActions}
            selectedRows={table.selectedRows}
            onSelectionChange={table.setSelectedRows}
            highlightedRows={table.highlightedRows}
            onHighlightChange={table.setHighlightedRows}
            emptyMessage={table.emptyMessage}
          />
        </div>
      )}

      <ReferralModal
        isOpen={list.modalOpen}
        onClose={list.closeReferral}
        referral={list.selectedReferral}
        onMarkComplete={list.handleMarkComplete}
        onUpdate={list.handleUpdateReferral}
        onDelete={list.handleDeleteReferral}
        loading={list.modalLoading}
        isReadOnly={!list.hasDocsWrite || !!list.selectedReferral?.isComplete}
        activeUsers={list.activeUsers}
        teams={list.teams}
      />

      <CreateReferralModal
        isOpen={list.createModalOpen}
        onClose={() => list.setCreateModalOpen(false)}
        onCreate={list.handleCreateReferral}
        loading={list.createLoading}
      />

      <BulkMarkCompleteReferralsModal
        isOpen={table.bulkMarkCompleteModalOpen}
        onClose={() => {
          table.setBulkMarkCompleteModalOpen(false);
          table.setReferralsForBulkAction([]);
        }}
        onConfirm={table.confirmBulkMarkComplete}
        referrals={table.referralsForBulkAction}
        loading={table.bulkMarkCompleteLoading}
      />

      <DeleteReferralModal
        isOpen={list.deleteModalOpen}
        onClose={() => {
          list.setDeleteModalOpen(false);
          list.setReferralToDelete(null);
        }}
        onConfirm={list.confirmDeleteReferral}
        referral={list.referralToDelete}
        loading={list.deleteLoading}
      />

      <ColumnCustomizationModal
        isOpen={table.columnCustomizationOpen}
        onClose={() => table.setColumnCustomizationOpen(false)}
        columns={table.columns}
        currentOrder={table.columnOrder}
        hiddenColumns={table.hiddenColumnsSet}
        defaultHidden={table.defaultHidden}
        onSave={(order, hidden) => {
          table.saveSettings(order, Array.from(hidden));
          table.setColumnCustomizationOpen(false);
        }}
      />
    </div>
  );
};

export default ReferralViewController;
